import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	forwardRef,
	memo,
	useCallback,
	useMemo,
} from 'react';
import { styled } from '@compiled/react';
import set from 'lodash/set';
import Button from '@atlaskit/button';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import { PopupSelect, components, type OptionProps } from '@atlaskit/select';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import {
	statusCategoryToThemeMap,
	STATUS_THEME_IN_PROGRESS,
	STATUS_THEME_DEFAULT,
	STATUS_THEME_SUCCESS,
	bgColor,
	bgHoverColor,
	textColor,
	textHoverColor,
} from '@atlassian/jira-common-styles/src/status';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { TOOLTIP_DELAY } from '../../../common/constants';
import type { StatusTransition } from '../../../common/types';
import messages from './messages';
import type { SelectRef, Props, StatusOption } from './types';

const ANALYTICS_SUBJECT = 'iccStatusSelect';
export const FIELD_KEY_STATUS_TRANSITIONS = 'statusTransitions';

const Option = ({ children, ...props }: OptionProps<StatusOption>) => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const { statusInfo } = props.data as StatusOption;

	const StyledOption = useMemo(
		() => OptionContentComponents[statusCategoryToThemeMap[statusInfo.statusCategoryId]],
		[statusInfo.statusCategoryId],
	);

	return (
		<components.Option {...props}>
			<StyledOption data-testId="platform-inline-card-create.ui.form.status-select.option">
				{children}
			</StyledOption>
		</components.Option>
	);
};

const transformOptions = (statusTransitions: StatusTransition[], selectedTransitionId?: number) => {
	let value;
	const options: StatusOption[] = [];

	statusTransitions.forEach((transition) => {
		const option = {
			label: transition.name,
			value: transition.transitionId,
			statusInfo: transition,
		};

		if (transition.transitionId === selectedTransitionId) {
			value = option;
		} else {
			options.push(option);
		}
	});

	if (!value) {
		value = options?.[0];
	}

	return { options, value };
};

const StatusSelect = forwardRef<SelectRef, Props>(
	({ statusTransitions, selectedTransitionId, onSelectTransition }, ref) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();

		const { value, options } = useMemo(
			() => transformOptions(statusTransitions, selectedTransitionId),
			[statusTransitions, selectedTransitionId],
		);

		const ButtonContent = useMemo(
			() => ButtonContentComponents[statusCategoryToThemeMap[value.statusInfo.statusCategoryId]],
			[value.statusInfo.statusCategoryId],
		);

		const trigger = useCallback(
			({ isOpen, ...triggerProps }: { isOpen: boolean }) => (
				<Box xcss={statusContainerStyles}>
					<Box xcss={statusButtonWrapperStyles}>
						<Tooltip content={value.label ?? null} delay={TOOLTIP_DELAY} hideTooltipOnClick>
							<StyledButton
								{...triggerProps}
								appearance="subtle"
								spacing="none"
								isSelected={isOpen}
								aria-label={formatMessage(messages.ariaLabel, {
									value: value.label ?? formatMessage(messages.invalid),
								})}
								testId="platform-inline-card-create.ui.form.status-select.trigger"
							>
								<ButtonContent>
									<ButtonLabel>{value.label}</ButtonLabel>
									<ChevronDownIcon label="" />
								</ButtonContent>
							</StyledButton>
						</Tooltip>
					</Box>
				</Box>
			),
			[formatMessage, value, ButtonContent],
		);

		const onChange = useCallback(
			(option: StatusOption | null) => {
				option && onSelectTransition(option.statusInfo.transitionId);

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'dropdown changed',
						actionSubject: ANALYTICS_SUBJECT,
					}),
					{},
				);
			},
			[createAnalyticsEvent, onSelectTransition],
		);

		const onMenuOpen = useCallback(() => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'dropdown opened',
					actionSubject: ANALYTICS_SUBJECT,
				}),
				{},
			);
		}, [createAnalyticsEvent]);

		const setRefNode = useCallback(
			(popupSelectRef: PopupSelect<StatusOption>) => {
				ref && popupSelectRef && set(ref, 'current.node', popupSelectRef);
			},
			[ref],
		);

		return (
			<PopupSelect
				ref={setRefNode}
				components={{ Option }}
				target={trigger}
				options={options}
				value={value}
				onChange={onChange}
				onMenuOpen={onMenuOpen}
				isSearchable={false}
				// DSP-2990 - PopupSelect doesn't respect isSearchable, searchThreshold is required
				searchThreshold={Number.MAX_SAFE_INTEGER}
				minMenuWidth="auto"
				classNamePrefix="inline-card-create-status-select"
				testId="platform-inline-card-create.ui.form.status-select.status-select"
			/>
		);
	},
);

const statusContainerStyles = xcss({
	display: 'flex',
	minWidth: '0px',
	position: 'relative',
});

const statusButtonWrapperStyles = xcss({
	width: '100%',
	paddingLeft: 'space.100',
});

export default memo<
	JSX.LibraryManagedAttributes<typeof StatusSelect, ComponentProps<typeof StatusSelect>>
>(StatusSelect);

export type { SelectRef, Props } from './types';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	width: '100%',
});

const buttonContentStyles = {
	display: 'flex',
	alignItems: 'center',
	paddingLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '11px',
	textTransform: 'uppercase',
	fontWeight: 700,
	borderRadius: token('border.radius', '3px'),
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContentInProgress = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...buttonContentStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: bgColor[STATUS_THEME_IN_PROGRESS],
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: textColor[STATUS_THEME_IN_PROGRESS],
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: textHoverColor[STATUS_THEME_IN_PROGRESS],
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundColor: bgHoverColor[STATUS_THEME_IN_PROGRESS],
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContentDefault = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...buttonContentStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: bgColor[STATUS_THEME_DEFAULT],
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: textColor[STATUS_THEME_DEFAULT],
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: textHoverColor[STATUS_THEME_DEFAULT],
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundColor: bgHoverColor[STATUS_THEME_DEFAULT],
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonContentSuccess = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...buttonContentStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: bgColor[STATUS_THEME_SUCCESS],
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: textColor[STATUS_THEME_SUCCESS],
	'&:hover': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		color: textHoverColor[STATUS_THEME_SUCCESS],
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundColor: bgHoverColor[STATUS_THEME_SUCCESS],
	},
});

type ButtonContent =
	| typeof ButtonContentSuccess
	| typeof ButtonContentDefault
	| typeof ButtonContentInProgress;

const ButtonContentComponents: { [key: string]: ButtonContent } = {
	[STATUS_THEME_IN_PROGRESS]: ButtonContentInProgress,
	[STATUS_THEME_DEFAULT]: ButtonContentDefault,
	[STATUS_THEME_SUCCESS]: ButtonContentSuccess,
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ButtonLabel = styled.span({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	minWidth: 0,
});

const optionContentStyles = {
	textTransform: 'uppercase',
	fontWeight: 700,
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
	fontSize: '11px',
	padding: `0 ${token('space.050', '4px')}`,
	borderRadius: token('border.radius', '3px'),
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionContentInProgress = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...optionContentStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: bgColor[STATUS_THEME_IN_PROGRESS],
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: textColor[STATUS_THEME_IN_PROGRESS],
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionContentDefault = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...optionContentStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: bgColor[STATUS_THEME_DEFAULT],
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: textColor[STATUS_THEME_DEFAULT],
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionContentSuccess = styled.span({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	...optionContentStyles,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	backgroundColor: bgColor[STATUS_THEME_SUCCESS],
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: textColor[STATUS_THEME_SUCCESS],
});

type OptionContent =
	| typeof OptionContentSuccess
	| typeof OptionContentDefault
	| typeof OptionContentInProgress;

const OptionContentComponents: { [key: string]: OptionContent } = {
	[STATUS_THEME_IN_PROGRESS]: OptionContentInProgress,
	[STATUS_THEME_DEFAULT]: OptionContentDefault,
	[STATUS_THEME_SUCCESS]: OptionContentSuccess,
};
